// UI 언어 store. currency.store.ts 패턴과 동일(zustand + localStorage).
//
// 결정 순서: ?lang 쿼리 → localStorage['korehalal_lang'] → navigator.language → 'en'.
// 인앱 웹은 앱이 WebView URL 에 ?lang 을 실어 보내므로(앱↔웹 동기화) 최우선.
// ?lang 수신 시 localStorage 에 영속해 SPA 내부 이동·재진입에도 유지된다.
import { create } from 'zustand';

const LANG_KEY = 'korehalal_lang';

// UI 로케일 = 앱 easy_localization 세트(en/ko/id/ms) + 아랍어.
// ⚠️ communityLabels.ts 의 TRANSLATION_LANGUAGES(15개)는 콘텐츠 번역 대상이라 별개다.
export const UI_LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'ko', label: '한국어' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'ar', label: 'العربية' },
] as const;

export type UiLang = (typeof UI_LOCALES)[number]['code'];

const SUPPORTED = UI_LOCALES.map((l) => l.code) as readonly string[];

// 노출 허용 UI 로케일 — 현재 전체 노출. 특정 언어를 다시 감추려면 이 배열에서
// 코드를 빼면 그 언어가 en 으로 폴백되고 선택기에서도 사라진다(초안 검수 게이트 용도).
export const ENABLED_LOCALES: readonly string[] = ['en', 'ko', 'id', 'ms', 'ar'];

export function isSupportedLang(code: string | null | undefined): code is UiLang {
  return !!code && SUPPORTED.includes(code);
}

export function isEnabledLang(code: string | null | undefined): code is UiLang {
  return isSupportedLang(code) && ENABLED_LOCALES.includes(code);
}

export function isRtl(code: string): boolean {
  return code === 'ar';
}

export function resolveInitialLang(): UiLang {
  if (typeof window === 'undefined') return 'en';

  // 선호 언어 결정(?lang → localStorage → navigator). ?lang 은 그대로 영속 —
  // 검수 후 해당 언어를 켜면 사용자의 기존 선택이 즉시 복원된다.
  let pref: UiLang | null = null;
  const q = new URLSearchParams(window.location.search).get('lang');
  const qCode = q?.toLowerCase().split('-')[0];
  if (isSupportedLang(qCode)) {
    localStorage.setItem(LANG_KEY, qCode);
    pref = qCode;
  } else {
    const stored = localStorage.getItem(LANG_KEY);
    if (isSupportedLang(stored)) {
      pref = stored;
    } else {
      const nav = (navigator.language || navigator.languages?.[0] || 'en')
        .toLowerCase()
        .split('-')[0];
      if (isSupportedLang(nav)) pref = nav;
    }
  }

  // 활성(검수 완료) 로케일만 실제 적용 — 미검수 초안 언어는 en 으로 폴백.
  return isEnabledLang(pref) ? pref : 'en';
}

type LangState = {
  lang: UiLang;
  setLang: (code: UiLang) => void;
};

export const useLangStore = create<LangState>((set) => ({
  lang: resolveInitialLang(),
  setLang: (code) => {
    set({ lang: code });
    if (typeof localStorage !== 'undefined') localStorage.setItem(LANG_KEY, code);
  },
}));
